import {
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ExclamationCircleIcon
} from '@heroicons/react/20/solid';
import { messageFromError, messageOfFailure } from '@oysterjs/core/graphql/errors';
import { Button } from '@oysterjs/ui/button';
import {
  Dropdown,
  DropdownButton,
  DropdownDescription,
  DropdownDivider,
  DropdownHeader,
  DropdownItem,
  DropdownLabel,
  DropdownMenu
} from '@oysterjs/ui/dropdown';
import React from 'react';
import {
  type AdminAnalyticsExportFieldsFragment,
  type AdminAnalyticsFilterInput,
  AdminAnalyticsExportStatus,
  AdminAnalyticsExportTable,
  GetAdminAnalyticsExportsDocument,
  useCreateAdminAnalyticsExportDownloadUrlMutation,
  useGetAdminAnalyticsExportsQuery,
  useStartAdminAnalyticsExportMutation
} from '~/api/graphql';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { downloadStoredFile } from '~/lib/download';
import { dateTimeFormatter } from './analytics-constants';

/** How often the page checks on an export while one is being prepared. */
const EXPORT_STATUS_POLL_INTERVAL_MS = 5000;

const EXPORT_TABLES = [
  { table: AdminAnalyticsExportTable.Workspaces, label: 'Workspaces' },
  { table: AdminAnalyticsExportTable.Artifacts, label: 'Artifacts' }
];

const STATUS_TEXT_CLASS = 'text-xs text-neutral-500 dark:text-neutral-400';

const START_FAILED = "Couldn't start the export. Try again.";
const DOWNLOAD_FAILED = "Couldn't start the download. Try again.";
const OWN_AGENCY_ONLY_NOTE = 'Exports cover your own agency only. Choose This agency to export.';

/**
 * The Export CSV menu and the caller's latest export of each table. An export uses the
 * filters set on the page when it starts.
 */
export const AnalyticsExportMenu = ({
  filter
}: {
  filter: AdminAnalyticsFilterInput | undefined;
}) => {
  const { data, startPolling, stopPolling } = useGetAdminAnalyticsExportsQuery({
    fetchPolicy: 'cache-and-network'
  });
  const [startExport] = useStartAdminAnalyticsExportMutation({
    refetchQueries: [GetAdminAnalyticsExportsDocument]
  });
  const [startError, setStartError] = React.useState<string | null>(null);
  const [isStarting, setIsStarting] = React.useState(false);
  const canExportSubagencies = useFlag(FEATURE_FLAGS.ANALYTICS_CSV_EXPORT_SUBAGENCIES);
  const ownAgencyOnlyNoteId = React.useId();

  const exports = data?.adminAnalyticsExports ?? [];
  const latestExportOf = (table: AdminAnalyticsExportTable) =>
    exports.find((analyticsExport) => analyticsExport.table === table);
  const isPreparing = (table: AdminAnalyticsExportTable) =>
    latestExportOf(table)?.status === AdminAnalyticsExportStatus.Running;
  const isAnyPreparing = EXPORT_TABLES.some(({ table }) => isPreparing(table));
  const isScopedToSubagencies = Boolean(filter?.subagencyId || filter?.includeAllSubagencies);
  const isOwnAgencyOnly = isScopedToSubagencies && !canExportSubagencies;

  React.useEffect(() => {
    if (!isAnyPreparing) return undefined;
    startPolling(EXPORT_STATUS_POLL_INTERVAL_MS);
    return stopPolling;
  }, [isAnyPreparing, startPolling, stopPolling]);

  const start = async (table: AdminAnalyticsExportTable) => {
    // The item stays focusable while one prepares, so its click is ignored here.
    if (isPreparing(table) || isStarting) return;
    setStartError(null);
    setIsStarting(true);
    try {
      const result = await startExport({ variables: { input: { table, filter } } });
      setStartError(messageOfFailure(result, START_FAILED));
    } catch (error) {
      setStartError(messageFromError(error, START_FAILED));
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:ml-auto">
      <div role="status" aria-live="polite" className="flex flex-col gap-1 sm:items-end">
        {EXPORT_TABLES.map(({ table, label }) => {
          const latestExport = latestExportOf(table);
          return (
            latestExport && (
              <ExportStatus
                key={`${table}:${latestExport.id}`}
                label={label}
                analyticsExport={latestExport}
              />
            )
          );
        })}
      </div>
      {startError && <ExportProblem>{startError}</ExportProblem>}
      {isOwnAgencyOnly && (
        <p id={ownAgencyOnlyNoteId} className={STATUS_TEXT_CLASS}>
          {OWN_AGENCY_ONLY_NOTE}
        </p>
      )}
      <Dropdown>
        <DropdownButton
          color="white"
          loading={isStarting}
          disabled={isOwnAgencyOnly}
          aria-describedby={isOwnAgencyOnly ? ownAgencyOnlyNoteId : undefined}
        >
          <ArrowDownTrayIcon data-slot="icon" />
          Export CSV
          <ChevronDownIcon data-slot="icon" />
        </DropdownButton>
        <DropdownMenu anchor="bottom end">
          <DropdownHeader>
            <p className={STATUS_TEXT_CLASS}>
              Exports use live data, so recent activity may differ from the charts below.
            </p>
          </DropdownHeader>
          <DropdownDivider />
          {EXPORT_TABLES.map(({ table, label }) => (
            <DropdownItem
              key={table}
              aria-disabled={isPreparing(table) || undefined}
              onClick={() => start(table)}
            >
              <DropdownLabel>{label}</DropdownLabel>
              {isPreparing(table) && <DropdownDescription>Already preparing…</DropdownDescription>}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

/** Announced only when `isNew`, so a problem already there when the page loads isn't read out. */
const ExportProblem = ({
  children,
  isNew = true
}: {
  children: React.ReactNode;
  isNew?: boolean;
}) => (
  <p
    role={isNew ? 'alert' : undefined}
    className="flex items-center gap-1 text-xs text-rose-600 dark:text-rose-400"
  >
    <ExclamationCircleIcon className="size-4 shrink-0" aria-hidden />
    {children}
  </p>
);

const ExportStatus = ({
  label,
  analyticsExport
}: {
  label: string;
  analyticsExport: AdminAnalyticsExportFieldsFragment;
}) => {
  const [createDownloadUrl] = useCreateAdminAnalyticsExportDownloadUrlMutation();
  const [downloadError, setDownloadError] = React.useState<string | null>(null);
  // Keyed by export, so this is the status the export had when the page first showed it.
  const [firstSeenStatus] = React.useState(analyticsExport.status);
  // A ref rather than a disabled button, so focus stays on Download while the link is made.
  const isCreatingLinkRef = React.useRef(false);

  const download = async () => {
    if (isCreatingLinkRef.current) return;
    isCreatingLinkRef.current = true;
    setDownloadError(null);
    try {
      const result = await createDownloadUrl({ variables: { id: analyticsExport.id } });
      const url = result.data?.createAdminAnalyticsExportDownloadUrl;
      const failure = messageOfFailure(result, DOWNLOAD_FAILED);
      if (failure || !url) {
        setDownloadError(failure ?? DOWNLOAD_FAILED);
        return;
      }
      downloadStoredFile(url);
    } catch (error) {
      setDownloadError(messageFromError(error, DOWNLOAD_FAILED));
    } finally {
      isCreatingLinkRef.current = false;
    }
  };

  switch (analyticsExport.status) {
    case AdminAnalyticsExportStatus.Running:
      return <p className={STATUS_TEXT_CLASS}>Preparing {label.toLowerCase()} export…</p>;
    case AdminAnalyticsExportStatus.Ready:
      return (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-neutral-700 dark:text-neutral-300">
            {label} export ready.
          </span>
          <Button color="white" onClick={download}>
            Download
          </Button>
          {analyticsExport.expiresAt && (
            <span className={STATUS_TEXT_CLASS}>
              Expires {dateTimeFormatter.format(new Date(analyticsExport.expiresAt))}
            </span>
          )}
          {downloadError && <ExportProblem>{downloadError}</ExportProblem>}
        </div>
      );
    case AdminAnalyticsExportStatus.Failed:
      return (
        <ExportProblem isNew={firstSeenStatus === AdminAnalyticsExportStatus.Running}>
          {label} export failed. Try exporting again.
        </ExportProblem>
      );
    case AdminAnalyticsExportStatus.Expired:
      return (
        <p className={STATUS_TEXT_CLASS}>{label} export expired. Export again for a new file.</p>
      );
  }
};
